import Point from 'esri/geometry/Point';
import SpatialReference from 'esri/SpatialReference';
import { Promise as bbPromise } from 'promise';

import { criteriaStore } from 'stores/CriteriaStore';
import { reportStore } from 'stores/ReportStore';
import { esriQuery, esriIdentifyImageService } from 'js/utils/esriQueries';

import { standardsConversionInchesMMs, convserionMilesMeterSecond, conversionTemperature } from 'utils/Utilities';

import standardsConfig from 'js/standards.config';

const queryIce = (location, load) => {
  // Grabbing Standards Version and Measurement state
  const version = criteriaStore.getState().standardsVersion;
  const riskLevel = criteriaStore.getState().riskLevel;
  const measurement = criteriaStore.getState().measurement;

  const geometry = new Point(location.long, location.lat, new SpatialReference({ wkid: 4326 }));

  const promises = [];

  if (version === '7-22') {
    Object.keys(standardsConfig[version].iceServices).forEach((key) => {
      const url = standardsConfig[version].iceServices[key];
      if (url.includes('MapServer')) {
        promises.push(
          esriQuery(url, { outFields: ['*'], geometry, returnGeometry: false }).then((results) => {
            return { key, attributes: results.features.length > 0 ? results.features[0].attributes : null };
          })
        );
      } else {
        if (key.includes('riskCategory')) {
          if (key === `riskCategory${riskLevel}`) {
            promises.push(
              esriIdentifyImageService(url, { geometry }).then(({ value }) => {
                return { key, value };
              })
            );
          }
        } else {
          promises.push(
            esriIdentifyImageService(url, { geometry }).then(({ value }) => {
              return { key, value };
            })
          );
        }
      }
    });
  } else {
    const queryLayerId = standardsConfig[version].iceQueryLayerId;
    promises.push(
      esriQuery(load.layers.query.url + queryLayerId, {
        outFields: ['*'],
        geometry,
        returnGeometry: false,
      }).then((results) => {
        return {
          key: `riskCategory${riskLevel}`,
          attributes: results.features.length > 0 ? results.features[0].attributes : null,
        };
      })
    );
  }

  promises.push(
    esriQuery('https://gis.asce.org/arcgis/rest/services/ASCE/ASCE_Loading/MapServer/10', {
      outFields: ['*'],
      geometry,
      returnGeometry: false,
    }).then((results) => {
      return { key: 'state', attributes: results.features.length > 0 ? results.features[0].attributes : null };
    })
  );

  // Return the Promise
  return new bbPromise((resolve, reject) => {
    try {
      Promise.all(promises.map((promise) => promise.catch((e) => console.log(e)))).then((results) => {
        load.showInReport = true;

        let iceResult = results[0],
          stateResult = results[results.length - 1];

        // If no features are returned by Ice Query
        if ((version === '7-22' && iceResult.value === 'NoData') || (version !== '7-22' && !iceResult.attributes)) {
          load.sampleData.value = ['No Information Available'];
          load.details = [
            {
              type: 'singleLine',
              label: '',
              value: 'No Information Available',
            },
          ];

          load.details.push({
            type: 'note',
            note: 'Values provided are equivalent radial ice thicknesses due to freezing rain with concurrent 3-second gust speeds, for a 50-year mean recurrence interval, and temperatures concurrent with ice thicknesses due to freezing rain. Thicknesses for ice accretions caused by other sources shall be obtained from local meteorological studies. Ice thicknesses in exposed locations at elevations higher than the surrounding terrain and in valleys and gorges may exceed the mapped values.',
          });

          // Else if features are returned
        } else {
          let iceLoad, concTemp, gustSpeed, specZone;

          if (version === '7-22') {
            iceLoad = iceResult.value;

            const tempResult = results.find((result) => result.key === 'temp');
            if (tempResult && tempResult.attributes) {
              concTemp = tempResult.attributes[measurement === 'si' ? 'conc_temp_SI' : 'conc_temp'];
            }

            const specResult = results.find((result) => result.key === 'specialZones');
            if (specResult && specResult.attributes) {
              specZone = specResult.attributes.spec_zone;
            }

            const gustResult = results.find((result) => result.key === 'gust');
            if (gustResult) {
              gustSpeed = gustResult.value;
            }
          } else {
            iceLoad = iceResult.attributes.ice_load;
            concTemp = iceResult.attributes.conc_temp;
            gustSpeed = iceResult.attributes.gust_speed;
            specZone = iceResult.attributes.spec_zone;
          }

          // If there are features, first check if the location is in the special case of Hawaii
          if (stateResult.attributes && stateResult.attributes.STATE_NAME === 'Hawaii') {
            load.sampleData.value = ['Obtain from local meteorological studies.'];

            load.details = [
              {
                type: 'singleLine',
                label: 'Thickness',
                value: 'Obtain from local meteorological studies.',
              },
              {
                type: 'singleLine',
                label: 'Concurrent Temperature',
                value: conversionTemperature(32, 'imperial', measurement).toFixed(0),
              },
              {
                type: 'singleLine',
                label: standardsConfig[version].gustSpeed,
                value: 'Unavailable',
              },
            ];

            // If it is not in Hawaii
          } else {
            // Grab the iceResult values and convert as necessary
            let iceValue = 0;
            if (iceLoad > 0) {
              iceValue = standardsConversionInchesMMs(iceLoad, 'imperial', measurement);
              iceValue = measurement === 'si' ? iceValue.toFixed(0) : iceValue.toFixed(2);
            }
            if (version !== '7-22' || iceValue > 0) {
              iceValue += measurement === 'si' ? ' mm' : ' in.';
            }

            let iceTemp;

            if (version === '7-22') {
              iceTemp = concTemp;
            } else {
              iceTemp = conversionTemperature(concTemp, 'imperial', measurement).toFixed(0);
            }

            iceTemp += measurement === 'si' ? ' C' : ' F';

            let iceGustSpeed = convserionMilesMeterSecond(gustSpeed, 'imperial', measurement).toFixed(0);
            iceGustSpeed += measurement === 'si' ? ' mm/s' : ' mph';

            // Begin setting load information
            if (iceValue === 0 && version === '7-22') {
              iceValue = 'N/A';
              iceTemp = 'N/A';
              iceGustSpeed = 'N/A';
            }
            load.sampleData.value = [iceValue];
            load.details = [
              {
                type: 'singleLine',
                label: 'Thickness',
                value: iceValue,
              },
              {
                type: 'singleLine',
                label: 'Concurrent Temperature',
                value: iceTemp,
              },
              {
                type: 'singleLine',
                label: standardsConfig[version].gustSpeed,
                value: iceGustSpeed,
              },
            ];

            // Check to see if iceResult is located in a Special Zone
            // Special Case Appalachian Mountains
            if (specZone === 'Appalachian Mountains') {
              load.sampleData.value.push('Please see note in details.');
              load.details.push({
                type: 'stateNote',
                label: 'Note',
                note: {
                  startingText:
                    'In the Appalachian Mountains, ice thicknesses may vary significantly over short distances.',
                  value: 'In the Appalachian Mountains, ice thicknesses may vary significantly over short distances.',
                  hyperlinkText: '',
                  hyperlinkUrl: '',
                  endingText: '',
                },
              });
            }
            // Special Case Mountain West
            if (specZone === 'Mountain West') {
              load.sampleData.value.push('Please see note in details.');
              let elevationText = measurement === 'imperial' ? '5,000 ft' : '1,524 m';
              load.details.push({
                type: 'stateNote',
                label: 'Note',
                note: {
                  startingText: `In the mountain west, ice thicknesses may exceed the mapped values in the foothills and passes. However, at elevations above ${elevationText}, freezing rain is unlikely.`,
                  value: `In the mountain west, ice thicknesses may exceed the mapped values in the foothills and passes. However, at elevations above ${elevationText}, freezing rain is unlikely.`,
                  hyperlinkText: '',
                  hyperlinkUrl: '',
                  endingText: '',
                },
              });
            }

            // Push additional notes based on standardsVersion
            load.details.push(standardsConfig[version].iceNotes);

            // Lastly push hyperlink note
            load.details.push({
              type: 'dataSource',
              dataSource: {
                hyperlinkUrl: '',
                hyperlinkText: `Standard ASCE/SEI ${version}, Figs. 10-2 through 10-8`,
              },
            });
          }
        }
        // Emit changes to the data store
        reportStore.emitChange();
        // Resolving the promise
        resolve({ load: load.label });
      });
    } catch (error) {
      reject({ load: load.label, error });
    }
  });
};

export default queryIce;
